
import React, { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/auth";
import AdminUsersTable from "@/components/admin/AdminUsersTable";
import AdminStats from "@/components/admin/AdminStats";
import PendingRegistrationsTable from "@/components/admin/PendingRegistrationsTable";
import PendingUniversitiesTable from "@/components/admin/PendingUniversitiesTable";
import InstallAppDialog from "@/components/admin/InstallAppDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const AdminPage = () => {
  const { user, profile, isAdmin, refreshProfile, isLoading } = useAuth();
  
  // Debug logging for the specific admin user
  useEffect(() => {
    if (user?.id === 'd38f6842-78b6-4aad-9f42-6b295b3427f2') {
      console.log('🔍 ADMIN DEBUG - User state:', {
        userId: user?.id,
        userEmail: user?.email,
        profileExists: !!profile,
        profileData: profile,
        isAdmin: isAdmin,
        profileIsAdmin: profile?.is_admin,
        isLoading: isLoading,
        approvalStatus: profile?.approval_status
      });
    }
  }, [user, profile, isAdmin, isLoading]);

  // Attempt to refresh the profile if needed
  useEffect(() => {
    if (user && !profile && !isLoading) {
      console.log("AdminPage: Profile missing, attempting refresh");
      refreshProfile();
    }
  }, [user, profile, refreshProfile, isLoading]);

  // Handle force refresh
  const handleForceRefresh = async () => {
    if (user) {
      toast.info("Refreshing profile data...");
      
      // Clear profile cache first
      try {
        const { clearProfileCache } = await import('@/services/profiles');
        clearProfileCache(user.id);
        console.log('Profile cache cleared for user:', user.id);
      } catch (error) {
        console.error('Error clearing profile cache:', error);
      }
      
      // Then refresh profile
      await refreshProfile();
    }
  };

  // Early return with helpful message if no user or admin status is still loading
  if (isLoading) {
    return <div className="container mx-auto py-8 px-4 text-center">Loading user information...</div>;
  }

  if (!user) {
    return <div className="container mx-auto py-8 px-4 text-center">
      You need to be logged in to access this page.
    </div>;
  }

  // If we have a user but no profile yet, show loading with refresh option
  if (!profile) {
    return <div className="container mx-auto py-8 px-4 text-center">
      <p className="mb-4">Waiting for admin profile data to load...</p>
      <Button onClick={handleForceRefresh} variant="outline" className="flex items-center gap-2">
        <RefreshCw className="h-4 w-4" />
        Retry Loading Profile
      </Button>
    </div>;
  }

  // Ensure admin access - use both profile and isAdmin flag from context
  if (!profile.is_admin && !isAdmin) {
    return <div className="container mx-auto py-8 px-4 text-center">
      You don't have admin permissions to access this page.
    </div>;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Install App Dialog - only visible for admins on mobile devices */}
      <InstallAppDialog isAdmin={true} />
      
      <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>
      
      <div className="grid gap-6 mb-8">
        <AdminStats />
      </div>
      
      <div className="grid gap-6">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Pending Registrations</CardTitle>
            <CardDescription>
              Review and approve new user registrations. Users cannot access the platform until approved.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PendingRegistrationsTable />
          </CardContent>
        </Card>

        <Card className="w-full">
          <CardHeader>
            <CardTitle>Pending Universities</CardTitle>
            <CardDescription>
              Review and approve new university submissions. Users can only select approved universities.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PendingUniversitiesTable />
          </CardContent>
        </Card>

        <Card className="w-full">
          <CardHeader>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              View and manage all registered users in the system. You can edit user roles, credits, and account status.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AdminUsersTable />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPage;
